// Library imports
import axios from 'axios';
import groupBy from 'lodash/groupBy';
import includes from 'lodash/includes';

// Project imports
import * as types from './actionTypes';
import { ROOT_URL, API_GATEWAY_URL } from './urlTypes';

import { toastError, toastWarning } from '../util/toastUtils';
import {
  ieSteps,
  _getSurveyQnO,
  _getIeDictionary,
  _splitExpression,
  _createExpression,
  _createFeasibilityExpression,
  _createTargetingCriteria,
  _getDefaultExpression,
  _getActiveTc,
  _getActivateMultipleTc,
  _updateTc,
  _setSelectedTc,
  _sortStudy,
  _updateDictionaryText,
  _isSurveyNameDuplicate,
} from '../util/IEUtils';

export const setActiveStep = step => dispatch => {
  dispatch({
    type: types.SET_IE_ACTIVE_STEP,
    step,
  });
};

export const resetState = (navFlow = false) => dispatch => {
  dispatch({
    type: types.RESET_IE_STATE,
    navFlow,
  });
};

export const resetSurveyInfo = () => dispatch => {
  dispatch({
    type: types.RESET_IE_SURVEY_INFO,
  });
};

export const resetSurveyEdit = () => dispatch => {
  dispatch({
    type: types.IE_EDIT_SURVEY_SUCCESS,
    data: null,
  });
};

export const setIEStudies = studies => {
  return {
    type: types.IE_STUDIES_SUCCESS,
    studies,
  };
};

export const setIEStudiesRequest = () => ({
  type: types.IE_STUDIES_REQUEST,
});

export const setIEStudiesFailure = () => ({
  type: types.IE_STUDIES_FAILURE,
});

export const setSurveyInfo = info => dispatch => {
  dispatch({
    type: types.SET_IE_SURVEY_INFO,
    info,
  });
};

export const getIEStudies = async accountId => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/ie-studies/${accountId}`;
  try {
    const response = await axios({
      url,
      method: 'get',
      withCredentials: true,
      responseType: 'json',
    });
    const data = response.data.data || [];
    return data;
  } catch (error) {
    const errorMessage = 'IE Studies - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const setIECategoriesRequest = () => {
  return {
    type: types.SET_IE_CATEGORIES_REQUEST,
  };
};
export const setIECategoriesSuccess = categories => {
  return {
    type: types.SET_IE_CATEGORIES_SUCCESS,
    categories,
  };
};
export const setIECategoriesFailure = () => {
  return {
    type: types.SET_IE_CATEGORIES_FAILURE,
  };
};

export const getCategories = async sti => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/study/${sti}/categories`;
  try {
    const response = await axios({
      url,
      method: 'get',
      withCredentials: true,
      responseType: 'json',
    });
    const data = response.data.data || [];
    return data;
  } catch (error) {
    const errorMessage = 'IE Categories - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const getIECategories = sti => async dispatch => {
  dispatch(setIECategoriesRequest());
  try {
    const categories = await getCategories(sti);
    if (categories) {
      dispatch(setIECategoriesSuccess(categories));
    } else {
      dispatch(setIECategoriesFailure());
    }
  } catch (error) {
    dispatch(setIECategoriesFailure());
  }
};

export const setIESurvey = surveyList => {
  return {
    type: types.IE_SURVEY_LIST_SUCCESS,
    surveyList,
  };
};

export const setSurveyListRequest = () => ({
  type: types.IE_SURVEY_LIST_REQUEST,
});

export const setIESurveyFailure = () => ({
  type: types.IE_SURVEY_LIST_FAILURE,
});

export const getSurveyList = async accountId => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/accounts/${accountId}/surveys`;
  try {
    const response = await axios({
      url,
      method: 'get',
      withCredentials: true,
      responseType: 'json',
    });
    const data = response.data.data || [];
    if (response.data.data) {
      return data;
    }
    const errorMessage = response.data.message || 'Simmons Survey List - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  } catch (error) {
    const errorMessage = 'Simmons Survey List - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const setSurveyDefaultStudy = study => dispatch => {
  // default study should be the latest study.
  const { pn, sti, sd, wt, dv, st } = study;
  dispatch(
    setSurveyInfo({
      study: {
        pn,
        sti,
        sd,
        wt,
        dv,
        st,
      },
    })
  );
};

export const getIEInitialData = accountId => async dispatch => {
  let studies = [];
  try {
    dispatch(setIEStudiesRequest());
    studies = await getIEStudies(accountId);
    if (studies) {
      const sortedStudies = _sortStudy(studies);
      dispatch(setIEStudies(sortedStudies));
      if (sortedStudies.length > 0) {
        const study = sortedStudies[0];
        dispatch(setSurveyDefaultStudy(study));
        dispatch(getIECategories(study.sti));
      }
    }
  } catch (error) {
    console.warn(error);
    dispatch(setIEStudiesFailure());
  }

  if (studies.length > 0) {
    try {
      dispatch(setSurveyListRequest());
      const surveyList = await getSurveyList(accountId);
      if (surveyList) {
        dispatch(setIESurvey(surveyList));
      } else {
        dispatch(setIESurveyFailure());
      }
    } catch (error) {
      console.warn(error);
      dispatch(setIESurveyFailure());
    }
  } else {
    dispatch(setIEStudiesFailure());
  }
};

export const setSurveyDetails = survey => dispatch => {
  dispatch({
    type: types.SET_IE_SURVEY_DETAILS,
    survey,
  });
};

export const getSurveyLink = async surveyId => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/surveylink/${surveyId}`;
  try {
    const response = await axios({
      url,
      method: 'get',
      withCredentials: true,
      responseType: 'json',
    });
    return response.data.data;
  } catch (error) {
    const errorMessage = 'SurveyLink - Survey link not found.';
    toastError(errorMessage);
    return false;
  }
};

export const setSurveyName = surveyName => dispatch => {
  dispatch({
    type: types.SET_IE_SURVEY_NAME,
    surveyName,
  });
};

export const setIEKeywordsSuccess = keywords => ({
  type: types.IE_KEYWORDS_SUCCESS,
  keywords,
});

export const setIeTcRequest = () => ({
  type: types.IE_TARGET_REQUEST,
});

export const setIeTcSuccess = (sti, tc) => ({
  type: types.IE_TARGET_SUCCESS,
  tc,
  sti,
});

export const setIeTcFailure = () => ({
  type: types.IE_TARGET_FAILURE,
});

export const updateSelectedTc = selectedTargets => ({
  type: types.SET_IE_SELECTED_TARGETS,
  selectedTargets,
});

export const setSamplewgtd = data => dispatch => {
  dispatch({
    type: types.SET_IE_SAMPLE_WGTD,
    data,
  });
};
export const setCrosstabIR = data => dispatch => {
  dispatch({
    type: types.SET_IE_CROSSTAB,
    data,
  });
};
export const setIECompletes = completes => dispatch => {
  dispatch({
    type: types.SET_IE_COMPLETES,
    completes,
  });
};

export const setIEIRSSSuccess = (
  expression,
  feasibilityExpression,
  tcExp,
  tc,
  incidenceRate,
  requiredCompletes,
  estimatedCost
) => async dispatch => {
  const promise = new Promise(resolve => {
    dispatch({
      type: types.GET_IE_IR_SS_SUCCESS,
    });

    setTimeout(() => {
      resolve('done!');
    }, 200);
  });

  const result = await promise; // wait until the promise resolves (*)
  dispatch(
    setSurveyInfo({
      expression: expression || '',
      feasibilityExpression,
      targetingCriteria: tcExp || '',
      estimatedCost,
      incidenceRate,
      requiredCompletes,
    })
  );
  dispatch(updateSelectedTc(tc));

  return result;
};

export const setIEIRSSFaiure = () => (dispatch, getState) => {
  const prevState = getState();
  const sti = prevState.turnkeyOmnibus.surveyInfo.study.sti;
  const stc = prevState.turnkeyOmnibus.selectedTargets;
  const tc = prevState.turnkeyOmnibus.targetingCriteria.data;

  // if (stc.length > 0) {
  const targetingCriteria = tc.map(q => {
    const options = q.options.map(op => {
      const isActive = stc.filter(item => item.oNode === op.oNode);
      return {
        ...op,
        isActive: isActive.length > 0,
        isSelected: false,
      };
    });
    return {
      ...q,
      options,
    };
  });
  dispatch(setIeTcSuccess(sti, targetingCriteria));
  // }
  dispatch({
    type: types.GET_IE_IR_SS_FAILURE,
  });
};

export const getSurveyCalculation = async request => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/survey/calculations`;
  try {
    const response = await axios({
      url,
      method: 'post',
      withCredentials: true,
      data: request,
      responseType: 'json',
    });
    const responseData = response.data.data;
    if (response.data.message === 'SUCCESS' && responseData) {
      return responseData;
    }
    const errorMessage = response.data.message || 'Survey Calculation fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  } catch (error) {
    const errorMessage = 'Survey Calculation fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const updateExpressions = tc => async (dispatch, getState) => {
  dispatch({
    type: types.GET_IE_IR_SS_REQUEST,
  });

  const prevState = getState();

  const pn = prevState.turnkeyOmnibus.surveyInfo.study.pn;
  const study = prevState.turnkeyOmnibus.ieStudy.data.filter(
    item => item.pn === pn
  );
  const targetingCriteria = prevState.turnkeyOmnibus.targetingCriteria.data;
  const { currentAccountId } = prevState.login.loginResponse.data;

  const expression = _createExpression(tc);
  const feasibilityExpression = _createFeasibilityExpression(tc);
  const tcExp = _createTargetingCriteria(tc);

  const { defaultExp, defaultFeasibilityExp } = _getDefaultExpression(
    targetingCriteria
  );

  const calculationRequest = {
    accountId: currentAccountId,
    productNo: pn,
    weightType: study.wt,
    divisor: 0,
    expression: [tc && tc.length > 0 ? expression : defaultExp],
    prodegeExpression:
      tc && tc.length > 0 ? feasibilityExpression : defaultFeasibilityExp,
  };

  try {
    const calculations = await getSurveyCalculation(calculationRequest);
    if (calculations) {
      const {
        requiredCompletes = null,
        incidenceRate = null,
        estimatedCost = null,
        samplewgtd = [],
        crosstab = null,
        prodegefeasibilityrun = null,
      } = calculations;
      dispatch(setSamplewgtd(samplewgtd[0]));
      dispatch(setCrosstabIR(crosstab));
      const completesEstimate = prodegefeasibilityrun
        ? prodegefeasibilityrun.completes_estimate
        : 0;
      dispatch(setIECompletes(completesEstimate));

      dispatch(
        setIEIRSSSuccess(
          expression,
          feasibilityExpression,
          tcExp,
          tc,
          incidenceRate,
          requiredCompletes,
          estimatedCost
        )
      );
    } else {
      dispatch(setIEIRSSFaiure());
    }
  } catch (error) {
    dispatch(setIEIRSSFaiure());
  }
};

export const setSelectedTc = selectedTargets => (dispatch, getState) => {
  const prevState = getState();
  const stc = prevState.turnkeyOmnibus.selectedTargets;
  const tc = [...stc, ...selectedTargets];
  dispatch(updateExpressions(tc));
};

export const removeSelectedTc = to => (dispatch, getState) => {
  const prevState = getState();
  const sti = prevState.turnkeyOmnibus.surveyInfo.study.sti;
  const allTc = prevState.turnkeyOmnibus.targetingCriteria.data;
  const stc = prevState.turnkeyOmnibus.selectedTargets;
  const targetingCriteria = _updateTc(allTc, to);
  const removedTc = stc.filter(item => item.oNode !== to.oNode);
  const tc = removedTc;
  dispatch(updateExpressions(tc));
  if (targetingCriteria.length > 0) {
    dispatch(setIeTcSuccess(sti, targetingCriteria));
  }
};

export const activateTC = (tc = []) => (dispatch, getState) => {
  const prevState = getState();
  const sti = prevState.turnkeyOmnibus.surveyInfo.study.sti;
  const allTc = prevState.turnkeyOmnibus.targetingCriteria.data;
  const { targetingCriteria, selectedTc } = _getActiveTc(tc, allTc);
  if (selectedTc.length > 0) {
    dispatch(setSelectedTc(selectedTc));
  }

  if (targetingCriteria.length > 0) {
    dispatch(setIeTcSuccess(sti, targetingCriteria));
  }
};

export const makeMultipleTCSelection = (qNode, range, isShiftClick) => (
  dispatch,
  getState
) => {
  const prevState = getState();
  const sti = prevState.turnkeyOmnibus.surveyInfo.study.sti;
  const allTc = prevState.turnkeyOmnibus.targetingCriteria.data;
  const targetingCriteria = _getActivateMultipleTc(
    allTc,
    qNode,
    range,
    isShiftClick
  );
  if (targetingCriteria.length > 0) {
    dispatch(setIeTcSuccess(sti, targetingCriteria));
  }
};

export const toggleTc = qid => (dispatch, getState) => {
  const prevState = getState();
  const sti = prevState.turnkeyOmnibus.surveyInfo.study.sti;
  const allTc = prevState.turnkeyOmnibus.targetingCriteria.data;
  const targetingCriteria = [];
  allTc.map(item => {
    const data = {
      ...item,
    };
    if (item.qid === qid) {
      data.isOpen = !data.isOpen;
    }
    targetingCriteria.push(data);
    return false;
  });
  if (targetingCriteria.length > 0) {
    dispatch(setIeTcSuccess(sti, targetingCriteria));
  }
};

export const updateTc = (tc, updateType = 'TOGGLE') => dispatch => {
  if (updateType === 'TOGGLE') {
    dispatch(toggleTc(tc.qid));
  }

  if (updateType === 'SELECTION') {
    const { qNode, range, isShiftClick = false } = tc;
    dispatch(makeMultipleTCSelection(qNode, range, isShiftClick));
  }

  if (updateType === 'CLICK') {
    dispatch(activateTC([tc]));
  }
};

export const getIEKeywordMapping = async (sti, pn) => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/mappings/${sti}/${pn}`;
  try {
    const response = await axios({
      url,
      method: 'get',
      withCredentials: true,
      responseType: 'json',
    });
    const data = response.data.data || {};
    return data;
  } catch (error) {
    const errorMessage = 'IE Keywords Mapping Fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const getTargettingCriteria = sti => async (dispatch, getState) => {
  const prevState = getState();
  const expression = prevState.turnkeyOmnibus.surveyInfo.expression;
  const splitedExp = expression && _splitExpression(expression);
  const productNo = prevState.turnkeyOmnibus.surveyInfo.study.pn;

  let keywordMapping = [];
  dispatch(setIeTcRequest());

  try {
    keywordMapping = await getIEKeywordMapping(sti, productNo);
    if (keywordMapping.length > 0) {
      dispatch(setIEKeywordsSuccess(keywordMapping));
    }
  } catch (error) {
    dispatch(setIeTcFailure());
  }

  if (keywordMapping.length > 0) {
    const { targetingCriteria, selectedTc } = _setSelectedTc(
      keywordMapping,
      splitedExp
    );
    dispatch(setIeTcSuccess(sti, targetingCriteria));
    dispatch(updateExpressions(selectedTc));
  } else {
    dispatch(setIeTcFailure());
  }
};

export const clearTargettingCriteria = () => async dispatch => {
  const promise = new Promise(resolve => {
    dispatch(updateSelectedTc([]));
    dispatch(
      setSurveyInfo({
        expression: '',
        targetingCriteria: '',
        requiredCompletes: 2000,
        incidenceRate: 100,
      })
    );
    dispatch(setIeTcRequest());
    dispatch(setIEKeywordsSuccess([]));
    setTimeout(() => {
      resolve('done!');
    }, 500);
  });

  const result = await promise; // wait until the promise resolves (*)

  return result;
};

export const setEditSurvey = state => ({
  type: types.IE_EDIT_SURVEY,
  state,
});

export const ieEditSurveySuccess = data => ({
  type: types.IE_EDIT_SURVEY_SUCCESS,
  data,
});

export const getIESurvey = data => async (dispatch, getState) => {
  if (data) {
    const { turnkeyOmnibus } = getState();
    const selectedStudy = turnkeyOmnibus.ieStudy.data.find(
      c => c.pn === data.productNo
    );
    if (selectedStudy) {
      dispatch(setSurveyDefaultStudy(selectedStudy));
      dispatch(getIECategories(selectedStudy.sti));
    }
    const {
      accountId,
      categoryId,
      categoryName,
      expression = '',
      gizmoSurveyId,
      prodegeProjectId,
      surveyName,
      surveyId,
      targetingCriteria = '',
    } = data;
    const hasDefaultExp = includes(expression, 'ADULT18+');
    dispatch(
      setSurveyInfo({
        accountId,
        categoryId,
        categoryName,
        expression: hasDefaultExp ? '' : expression,
        gizmoSurveyId,
        prodegeProjectId,
        surveyName,
        surveyId,
        targetingCriteria: hasDefaultExp ? '' : targetingCriteria,
      })
    );
  }
  dispatch(ieEditSurveySuccess(data));
  return true;
};

export const enableEditSurvey = survey => async dispatch => {
  dispatch(setEditSurvey(true));
  dispatch(getIESurvey(survey));
  dispatch(setActiveStep(ieSteps.CREATE_SURVEY));
};

export const createSurveyRequest = () => dispatch => {
  dispatch({
    type: types.IE_CREATE_SURVEY_REQUEST,
  });
};

export const createSurveySuccess = () => dispatch => {
  dispatch({
    type: types.IE_CREATE_SURVEY_SUCCESS,
  });
};

export const createSurveyFailure = () => dispatch => {
  dispatch({
    type: types.IE_CREATE_SURVEY_FAILURE,
  });
};

export const postSurvey = async (data, surveyId, includedTC = false) => {
  let url = `${API_GATEWAY_URL}/api/instant-edge/survey`;
  if (surveyId) {
    url = `${API_GATEWAY_URL}/api/instant-edge/survey/${surveyId}?includedTC=${includedTC}`;
  }
  try {
    const response = await axios({
      url,
      method: surveyId ? 'PUT' : 'POST',
      withCredentials: true,
      responseType: 'json',
      data,
    });
    const responseData = response.data.data || null;
    if (response.data.data) {
      return responseData;
    }
    const errorMessage = response.data.message || 'Create Survey - Something went wrong, please try again later.';
    toastError(errorMessage);
    return null;
  } catch (error) {
    const errorMessage = error.response.data.message || 'Create Survey - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const handleCreateSurvey = (includedTC = false) => async (
  dispatch,
  getState
) => {
  const prevState = getState();
  const {
    surveyName,
    study,
    categoryId,
    surveyId,
    feasibilityExpression,
    requiredCompletes,
    estimatedCost,
    incidenceRate,
    targetingCriteria,
    expression,
    gizmoSurveyId,
    prodegeProjectId,
  } = prevState.turnkeyOmnibus.surveyInfo;

  const surveyList = prevState.turnkeyOmnibus.ieSurveyList.data;

  const tc = prevState.turnkeyOmnibus.targetingCriteria.data;
  const { defaultFeasibilityExp } = _getDefaultExpression(tc);

  const { currentAccountId } = prevState.login.loginResponse.data;

  const isDuplicate = _isSurveyNameDuplicate(surveyName, surveyId, surveyList);

  if (isDuplicate) {
    toastWarning(
      'Survey name already exist for this account, please choose a different name'
    );
    return false;
  }
  dispatch(createSurveyRequest());
  try {
    let requestData = {
      surveyName,
      accountId: currentAccountId,
      studyCode: study.st,
      productNo: study.pn,
      categoryId,
    };
    if (includedTC) {
      requestData = {
        surveyName,
        expression: expression || '',
        targetingCriteria: targetingCriteria || '',
        incidenceRate,
        estimatedCost: Number(estimatedCost),
        requiredCompletes,
        gizmoSurveyId,
        prodegeProjectId,
        expressions:
          feasibilityExpression.length > 0
            ? feasibilityExpression
            : defaultFeasibilityExp,
      };
    }

    const surveyResponse = await postSurvey(requestData, surveyId, includedTC);

    if (
      !surveyResponse ||
      !surveyResponse.surveyId ||
      surveyResponse.error ||
      (includedTC && surveyResponse && !surveyResponse.gizmoSurveyId)
    ) {
      dispatch(createSurveyFailure());
      return false;
    }

    dispatch(createSurveySuccess());
    dispatch(setEditSurvey(true));
    dispatch(
      setSurveyInfo({
        gizmoSurveyId: surveyResponse.gizmoSurveyId,
        prodegeProjectId: surveyResponse.prodegeProjectId,
        surveyId: surveyResponse.surveyId,
      })
    );
    dispatch(ieEditSurveySuccess(surveyResponse));
    return surveyResponse;
  } catch (error) {
    dispatch(createSurveyFailure());
    // const errorMessage =  'Create Survey - Something went wrong, please try again later.';
    // toastError(errorMessage);
    return false;
  }
};

export const setSurveyMetaDataRequest = () => dispatch => {
  dispatch({
    type: types.IE_SURVEY_METADATA_REQUEST,
  });
};

export const setSurveyMetaDataSuccess = metaData => dispatch => {
  dispatch({
    type: types.IE_SURVEY_METADATA_SUCCESS,
    metaData,
  });
};

export const setSurveyMetaDataQO = (questions, options) => dispatch => {
  dispatch({
    type: types.IE_SURVEY_METADATA_QO,
    questions,
    options,
  });
};

export const setSurveyMetaDataFailure = () => dispatch => {
  dispatch({
    type: types.IE_SURVEY_METADATA_FAILURE,
  });
};

export const updateDictionaryQnO = dictionary => dispatch => {
  dispatch({
    type: types.SET_IE_SURVEY_DICTIONARY,
    dictionary,
  });
};

export const updateInvalidQnO = ({ specialChar = [], maxLength = [] }) => dispatch => {
  dispatch({
    type: types.SET_IE_SURVEY_DICTIONARY_ERROR,
    specialChar,
    maxLength,
  });
};

export const getSurveyMetaData = async gizmoSurveyId => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/surveymetadata/${gizmoSurveyId}`;
  try {
    const response = await axios({
      url,
      method: 'PUT',
      withCredentials: true,
      responseType: 'json',
      data: {},
    });
    const metaData = response.data.data || [];
    return metaData;
  } catch (error) {
    const errorMessage = 'Survey Metadata fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

export const getIESurveyMetaData = gizmoSurveyId => async dispatch => {
  dispatch(setSurveyMetaDataRequest());
  try {
    const metaData = await getSurveyMetaData(gizmoSurveyId);
    const { questions, options } = await _getSurveyQnO(metaData);
    if (questions.length > 0) {
      const { dictionary, initialError } = _getIeDictionary(questions, options);
      dispatch(updateDictionaryQnO(dictionary));
      dispatch(updateInvalidQnO(initialError));
      dispatch(setSurveyMetaDataQO(questions, options));
      dispatch(setSurveyMetaDataSuccess(metaData));
    } else {
      dispatch(setSurveyMetaDataFailure());
    }
    return true;
  } catch (error) {
    console.warn(error);
    dispatch(setSurveyMetaDataFailure());
    return false;
  }
};

const removeKeyFromObj = (obj, prop) => {
  return Object.keys(obj).reduce((object, key) => {
    if (key !== prop) {
      object[key] = obj[key];
    }
    return object;
  }, {});
};

export const setIESurveyDictionaryText = (updatedQuestions, updatedOptions) => dispatch => {
  const questions = updatedQuestions.map(item =>
    removeKeyFromObj(item, 'updated')
  );
  const options = updatedOptions.map(item =>
    removeKeyFromObj(item, 'updated')
  );
  dispatch({
    type: types.SET_IE_SURVEY_DICTIONARY_TEXT,
    questions,
    options,
  });
};

export const setSurveyDictionayText = (text, type, currentObj) => (
  dispatch,
  getState
) => {
  const prevState = getState();
  const surveyQnO = prevState.turnkeyOmnibus.surveyQnO;
  const { data } = surveyQnO;
  const { questions, options } = _updateDictionaryText(
    text,
    type,
    currentObj,
    data
  );

  dispatch(setSurveyMetaDataQO(questions, options));

  const updatedOptions = options.filter(item => item.updated);
  const updatedQuestions = questions.filter(item => item.updated);
  dispatch(setIESurveyDictionaryText(updatedQuestions, updatedOptions));
};

export const updateSurveyDictionary = async data => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/surveydictionarytext`;
  try {
    const response = await axios({
      url,
      method: 'PUT',
      withCredentials: true,
      responseType: 'json',
      data,
    });
    // console.log(response.data);
    const responseData = response.data.message || [];
    return responseData;
    // return metaData;
  } catch (error) {
    const errorMessage = 'Saving Dictionary text fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
    // return false;
  }
};

export const saveSurveyDictionary = () => async (dispatch, getState) => {
  const prevState = getState();

  const surveyQnO = prevState.turnkeyOmnibus.surveyQnO;
  const metaData = prevState.turnkeyOmnibus.surveyQnO.metaData;
  const surveyQnOData = prevState.turnkeyOmnibus.surveyQnO.data;
  const { questions, options } = surveyQnO.updatedQnO;

  const data = [];

  let groupedQuestions = {};
  let groupedOptions = {};

  if (questions.length > 0) {
    groupedQuestions = groupBy(questions, 'questionId');
  }

  if (options.length > 0) {
    groupedOptions = groupBy(options, 'questionId');
  }

  metaData.map(item => {
    const questions = groupedQuestions[item.questionId] || [];
    const options = groupedOptions[item.questionId] || [];

    if (questions.length > 0 || options.length > 0) {
      const q = questions.length > 0 ? questions[0] : item;
      const nData = {
        ...q,
        optionDtoList: options,
      };
      data.push(nData);
    }
    return false;
  });
  dispatch(
    setSurveyInfo({
      noOfQuestions: metaData.length - 1,
    })
  );
  if (data.length > 0) {
    try {
      const isSaved = await updateSurveyDictionary(data);
      if (isSaved === 'SUCCESS') {
        // set updated dictionary text to question and options.
        const nQ = surveyQnOData.questions;
        const nO = surveyQnOData.options;
        const { dictionary, initialError } = _getIeDictionary(nQ, nO);
        dispatch(updateDictionaryQnO(dictionary));
        // set initial error if we have
        dispatch(updateInvalidQnO(initialError));

        // reset the dictionary updated text
        dispatch(setIESurveyDictionaryText([], []));
        return true;
      }
      return false;
    } catch (error) {
      console.warn(error);
      return false;
    }
  }

  return true;
};


export const submitSurveyForApproval = async data => {
  const url = `${API_GATEWAY_URL}/api/instant-edge/submitforapproval`;
  try {
    const response = await axios({
      url,
      method: 'POST',
      withCredentials: true,
      responseType: 'json',
      data,
    });
    const responseData = response.data.message;
    if (responseData === 'SUCCESS' && response.data.data) {
      return true;
    }
    const errorMessage = response.data.message || 'Submit Survey Fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  } catch (error) {
    const errorMessage = 'Submit Survey Fails - Something went wrong, please try again later.';
    toastError(errorMessage);
    return false;
  }
};

